import React from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons.jsx';
import { Avatar, confirmDialog } from '../components.jsx';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../auth/AuthContext.jsx';
import { CreateUserModal } from './settings-security.jsx';

const { useState, useEffect, useCallback, useMemo, useRef } = React;

const ROLE_OPTIONS = [
  ['admin', 'Admin', 'sem-violet', 'Acceso completo'],
  ['redactor', 'Redactor', 'sem-info', 'Edita memoria · sube docs'],
  ['revisor', 'Revisor', 'sem-warning', 'Comenta · aprueba'],
  ['firmante', 'Firmante', 'sem-success', 'Visto bueno final'],
  ['viewer', 'Viewer', 'sem-neutral', 'Sólo lectura'],
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r[0], r[1]]));
const ROLE_TONE = Object.fromEntries(ROLE_OPTIONS.map((r) => [r[0], r[2]]));

function initialsFrom(name) {
  if (!name) return '·';
  return String(name).trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '·';
}

function toast(text, icon = 'Check') {
  window.dispatchEvent(new CustomEvent('lizzi:toast', { detail: { text, icon } }));
}

// ===============================================
// Celda editable en línea (sin modal)
// ===============================================
function EditableCell({ value, onSave, type = 'text', placeholder, mono, validate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const start = () => { setDraft(value ?? ''); setEditing(true); };

  const commit = async () => {
    const next = (draft ?? '').trim();
    if (next === (value ?? '')) { setEditing(false); return; }
    if (validate) {
      const err = validate(next);
      if (err) { toast(err, 'AlertTriangle'); inputRef.current?.focus(); return; }
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      toast(e?.message || 'No se pudo guardar.', 'AlertTriangle');
      inputRef.current?.focus();
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        placeholder={placeholder}
        style={{
          width: '100%', background: 'var(--bg-1)', border: '1px solid var(--primary)',
          borderRadius: 6, padding: '5px 8px', fontSize: 12.5, color: 'var(--text-1)',
          fontFamily: mono ? 'JetBrains Mono' : 'inherit', opacity: saving ? 0.6 : 1,
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      title="Pulsa para editar"
      className="editable-cell"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
        border: '1px solid transparent', borderRadius: 6, padding: '4px 6px', cursor: 'text',
        fontSize: mono ? 12.5 : 'inherit', color: 'inherit', textAlign: 'left',
        fontFamily: mono ? 'JetBrains Mono' : 'inherit', maxWidth: '100%',
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value || <span style={{ color: 'var(--text-4)' }}>{placeholder || '—'}</span>}
      </span>
      <Icon.Edit size={11} style={{ color: 'var(--text-4)', flexShrink: 0 }} />
    </button>
  );
}

// ===============================================
// Etiqueta de rol con color + selector en línea
// ===============================================
function RoleTag({ role, disabled, saving, onChange }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuW = 220;
    const left = Math.min(r.left, window.innerWidth - menuW - 12);
    setCoords({ top: r.bottom + 6, left: Math.max(12, left), width: menuW });
  }, []);

  const toggle = () => {
    if (disabled || saving) return;
    if (!open) place();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled || saving}
        onClick={toggle}
        className={`sem ${ROLE_TONE[role]}`}
        title={disabled ? 'No puedes cambiar tu propio rol' : 'Cambiar rol'}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer', opacity: (disabled || saving) ? 0.6 : 1,
          border: '1px solid transparent', gap: 5,
        }}
      >
        {saving ? '…' : ROLE_LABEL[role]}
        {!disabled && <Icon.ChevronDown size={11} />}
      </button>
      {open && !disabled && coords && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed', top: coords.top, left: coords.left, zIndex: 1000, width: coords.width,
            background: 'var(--bg-1)', border: '1px solid var(--line-2)', borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.4)', padding: 6,
          }}
        >
          {ROLE_OPTIONS.map(([val, label, tone, desc]) => (
            <button
              key={val}
              type="button"
              onClick={() => { setOpen(false); onChange(val); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                background: val === role ? 'var(--bg-2)' : 'transparent', border: 0,
                borderRadius: 7, padding: '7px 8px', cursor: 'pointer',
              }}
            >
              <span className={`sem ${tone}`} style={{ pointerEvents: 'none' }}>{label}</span>
              <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{desc}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// ===============================================
// Equipo · roster real (solo administradores)
// ===============================================
function TeamScreen() {
  const { isAdmin, user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: err } = await supabase.functions.invoke('admin-list-users');
    setLoading(false);
    if (err) { setError('No se pudo cargar el equipo.'); return; }
    if (data?.error) { setError(typeof data.error === 'string' ? data.error : 'Acceso denegado.'); return; }
    setUsers(data?.users || []);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (isAdmin) load(); else setLoading(false);
  }, [isAdmin, authLoading, load]);

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    setSavingId(u.id);
    const { error: delErr } = await supabase.from('user_roles').delete().eq('user_id', u.id);
    if (delErr) { setSavingId(null); toast('No se pudo actualizar el rol.', 'AlertTriangle'); return; }
    const { error: insErr } = await supabase.from('user_roles').insert({ user_id: u.id, role: newRole });
    setSavingId(null);
    if (insErr) { toast('No se pudo actualizar el rol.', 'AlertTriangle'); return; }
    toast(`Rol de ${u.display_name || u.email} actualizado a ${ROLE_LABEL[newRole]}.`);
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role: newRole, roles: [newRole] } : x)));
  };

  const saveName = async (u, display_name) => {
    const { data, error: err } = await supabase.functions.invoke('admin-update-user', {
      body: { user_id: u.id, display_name },
    });
    if (err || data?.error) throw new Error(typeof data?.error === 'string' ? data.error : 'No se pudo actualizar el nombre.');
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, display_name } : x)));
    toast('Nombre actualizado.');
  };

  const saveEmail = async (u, email) => {
    const { data, error: err } = await supabase.functions.invoke('admin-update-user', {
      body: { user_id: u.id, email },
    });
    if (err || data?.error) throw new Error(typeof data?.error === 'string' ? data.error : 'No se pudo actualizar el correo.');
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, email } : x)));
    toast('Correo actualizado.');
  };

  const deleteUser = async (u) => {
    if (!(await confirmDialog({ title: 'Eliminar usuario', message: `¿Eliminar la cuenta de ${u.email}? Esta acción no se puede deshacer.`, confirmLabel: 'Eliminar', tone: 'danger', icon: 'Trash' }))) return;
    const { data, error: err } = await supabase.functions.invoke('admin-delete-user', { body: { user_id: u.id } });
    if (err || data?.error) { toast(data?.error || 'No se pudo eliminar.', 'AlertTriangle'); return; }
    toast('Usuario eliminado.');
    load();
  };

  const roleCounts = useMemo(() => {
    const c = {};
    ROLE_OPTIONS.forEach(([k]) => { c[k] = 0; });
    users.forEach((u) => { c[u.role] = (c[u.role] || 0) + 1; });
    return c;
  }, [users]);

  // --- No admin: acceso restringido ---
  if (!authLoading && !isAdmin) {
    return (
      <div className="viewport-inner fade-in">
        <div className="page-head">
          <div>
            <div className="page-eyebrow">Acceso restringido</div>
            <h1 className="page-title">Equipo</h1>
          </div>
        </div>
        <div className="card card-pad" style={{ padding: 60, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.Shield size={26} style={{ color: 'var(--text-3)' }} />
          </div>
          <div className="font-display-heavy" style={{ fontSize: 22 }}>Solo para administradores</div>
          <p style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 460 }}>
            La gestión del equipo, los roles y las cuentas está reservada a administradores. Puedes editar tu propio perfil desde Ajustes.
          </p>
          <button className="btn btn-outline" onClick={() => window.dispatchEvent(new CustomEvent('lizzi:goto-settings'))}>
            <Icon.Settings size={14} /> Ir a Ajustes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="viewport-inner fade-in">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">
            {loading ? 'Cargando equipo…' : `${users.length} ${users.length === 1 ? 'persona' : 'personas'} · ${new Set(users.map((u) => u.role)).size} roles`}
          </div>
          <h1 className="page-title">Equipo</h1>
          <p className="muted" style={{ fontSize: 14, marginTop: 2, maxWidth: 580 }}>
            Personas, roles y permisos del workspace. Pulsa el nombre o el correo para editarlos al instante.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Icon.Plus size={14} /> Añadir miembro
          </button>
        </div>
      </div>

      {error && (
        <div className="card card-pad" style={{ marginBottom: 16, color: 'var(--sem-danger)', fontSize: 13 }}>{error}</div>
      )}

      {/* KPIs por rol */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 22 }}>
        {ROLE_OPTIONS.map(([k, l, , desc], i) => (
          <div key={k} className="card card-pad row-enter" style={{ padding: '14px 16px', animationDelay: `${i * 50}ms` }}>
            <span className={`sem ${ROLE_TONE[k]}`}>{l}</span>
            <div className="font-display" style={{ fontSize: 28, marginTop: 8 }}>{loading ? '—' : roleCounts[k]}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{desc}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ overflow: 'visible' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Persona</th>
              <th style={{ width: 240 }}>Correo</th>
              <th style={{ width: 170 }}>Rol</th>
              <th style={{ width: 130 }}>Estado</th>
              <th style={{ width: 120, textAlign: 'center' }}>Propuestas activas</th>
              <th style={{ width: 120, textAlign: 'center' }}>Chats con Lizzi</th>
              <th style={{ width: 44 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ padding: 28, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Cargando equipo…</td></tr>
            )}
            {!loading && users.map((u, i) => {
              const avatarUser = { name: u.display_name || u.email, initials: initialsFrom(u.display_name || u.email), hue: u.avatar_hue ?? 200 };
              const isSelf = u.id === user?.id;
              return (
                <tr key={u.id} className="row-enter" style={{ animationDelay: `${i * 40}ms` }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar user={avatarUser} size={30} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <EditableCell
                            value={u.display_name || ''}
                            placeholder="Sin nombre"
                            validate={(v) => (v.length < 1 ? 'El nombre no puede estar vacío.' : '')}
                            onSave={(v) => saveName(u, v)}
                          />
                          {isSelf && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>(tú)</span>}
                        </div>
                        {u.title && <span style={{ fontSize: 12, color: 'var(--text-3)', paddingLeft: 6 }}>{u.title}</span>}
                      </div>
                    </div>
                  </td>
                  <td style={{ color: 'var(--text-3)' }}>
                    <EditableCell
                      value={u.email || ''}
                      type="email"
                      mono
                      placeholder="correo@…"
                      validate={(v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Introduce un correo válido.')}
                      onSave={(v) => saveEmail(u, v)}
                    />
                  </td>
                  <td>
                    <RoleTag role={u.role} disabled={isSelf} saving={savingId === u.id} onChange={(r) => changeRole(u, r)} />
                  </td>
                  <td>
                    {u.active ? (
                      <span className="sem sem-success" style={{ gap: 5 }}>
                        <Icon.CheckCircle size={11} /> Activa
                      </span>
                    ) : (
                      <span
                        className="sem sem-warning"
                        style={{ gap: 5 }}
                        title="La cuenta aún no se ha activado: el usuario no ha iniciado sesión ni configurado su contraseña."
                      >
                        <Icon.Clock size={11} /> Pendiente
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', fontFamily: 'JetBrains Mono', fontSize: 13 }}>{u.active_proposals}</td>
                  <td style={{ textAlign: 'center', fontFamily: 'JetBrains Mono', fontSize: 13 }}>{u.chats}</td>
                  <td style={{ textAlign: 'right' }}>
                    {!isSelf && (
                      <button className="btn btn-ghost btn-icon" onClick={() => deleteUser(u)} title="Eliminar usuario" style={{ color: 'var(--sem-danger)' }}>
                        <Icon.Trash size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && users.length === 0 && !error && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 13, padding: 28 }}>Aún no hay miembros. Añade el primero.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

window.TeamScreen = TeamScreen;

export { TeamScreen };
